import { supabase } from './supabaseClient';

/**
 * Cross-Device Sync Service
 * Handles syncing user data across devices via Supabase
 */

// =====================================================
// MEAL PLANS
// =====================================================

export const getMealPlan = async (userEmail) => {
  console.log('🔍 [MEAL PLAN] getMealPlan called with email:', userEmail);
  try {
    const { data, error } = await supabase
      .from('meal_plans')
      .select('*')
      .eq('user_email', userEmail)
      .order('added_at', { ascending: false });

    if (error) {
      console.error('❌ [MEAL PLAN] Database error:', error);
      throw error;
    }
    
    console.log('✅ [MEAL PLAN] Loaded from DB:', data?.length || 0, 'recipes');
    console.log('📊 [MEAL PLAN] Raw data:', data);
    
    // Return the recipe data from each entry
    const recipes = data?.map(item => ({
      ...item.recipe_data,
      _mealPlanId: item.id
    })) || [];
    
    console.log('📦 [MEAL PLAN] Returning:', recipes.length, 'recipes');
    return { 
      success: true, 
      data: recipes 
    };
  } catch (error) {
    console.error('❌ [MEAL PLAN] Error fetching meal plan:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const addToMealPlan = async (userEmail, recipe) => {
  console.log('➕ [MEAL PLAN] addToMealPlan called');
  console.log('📧 Email:', userEmail);
  console.log('🍽️ Recipe ID:', recipe.id);
  console.log('📝 Recipe name:', recipe.title || recipe.name);
  
  try {
    const payload = {
      user_email: userEmail,
      recipe_id: String(recipe.id),
      recipe_data: recipe
    };
    
    console.log('📤 [MEAL PLAN] Sending to DB:', payload);
    
    const { data, error } = await supabase
      .from('meal_plans')
      .upsert(payload, { onConflict: 'user_email,recipe_id' })
      .select()
      .single();

    if (error) {
      console.error('❌ [MEAL PLAN] Save failed:', error);
      console.error('❌ Error details:', JSON.stringify(error, null, 2));
      throw error;
    }
    
    console.log('✅ [MEAL PLAN] Saved successfully:', data?.id);
    return { success: true, data };
  } catch (error) {
    console.error('❌ [MEAL PLAN] Exception:', error);
    console.error('❌ Error message:', error.message);
    console.error('❌ Error code:', error.code);
    return { success: false, error: error.message };
  }
};

export const removeFromMealPlan = async (userEmail, recipeId) => {
  console.log('➖ [MEAL PLAN] removeFromMealPlan called');
  console.log('📧 Email:', userEmail);
  console.log('🍽️ Recipe ID:', recipeId);
  
  try {
    const { error } = await supabase
      .from('meal_plans')
      .delete()
      .eq('user_email', userEmail)
      .eq('recipe_id', String(recipeId));

    if (error) {
      console.error('❌ [MEAL PLAN] Delete failed:', error);
      throw error;
    }
    
    console.log('✅ [MEAL PLAN] Removed successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ [MEAL PLAN] Error removing from meal plan:', error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// SAVED RESOURCES
// =====================================================

export const getSavedResources = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('saved_resources')
      .select('resource_id')
      .eq('user_email', userEmail);

    if (error) throw error;
    return { 
      success: true, 
      data: data?.map(item => item.resource_id) || [] 
    };
  } catch (error) {
    console.error('Error fetching saved resources:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const saveResource = async (userEmail, resourceId) => {
  try {
    const { error } = await supabase
      .from('saved_resources')
      .upsert({
        user_email: userEmail,
        resource_id: resourceId
      }, { onConflict: 'user_email,resource_id' });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error saving resource:', error);
    return { success: false, error: error.message };
  }
};

export const unsaveResource = async (userEmail, resourceId) => {
  try {
    const { error } = await supabase
      .from('saved_resources')
      .delete()
      .eq('user_email', userEmail)
      .eq('resource_id', resourceId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error unsaving resource:', error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// SAVED FORUM POSTS
// =====================================================

export const getSavedForumPosts = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('saved_forum_posts')
      .select('post_id')
      .eq('user_email', userEmail);

    if (error) throw error;
    return { 
      success: true, 
      data: data?.map(item => item.post_id) || [] 
    };
  } catch (error) {
    console.error('Error fetching saved posts:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const saveForumPost = async (userEmail, postId) => {
  try {
    const { error } = await supabase
      .from('saved_forum_posts')
      .upsert({
        user_email: userEmail,
        post_id: postId
      }, { onConflict: 'user_email,post_id' });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error saving post:', error);
    return { success: false, error: error.message };
  }
};

export const unsaveForumPost = async (userEmail, postId) => {
  try {
    const { error } = await supabase
      .from('saved_forum_posts')
      .delete()
      .eq('user_email', userEmail)
      .eq('post_id', postId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error unsaving post:', error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// WEIGHT TRACKING
// =====================================================

export const getWeightHistory = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('weight_tracking')
      .select('*')
      .eq('user_email', userEmail)
      .order('recorded_at', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching weight history:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const addWeightEntry = async (userEmail, weight, unit = 'kg', notes = '') => {
  try {
    const { data, error } = await supabase
      .from('weight_tracking')
      .insert({
        user_email: userEmail,
        weight,
        unit,
        notes
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error adding weight entry:', error);
    return { success: false, error: error.message };
  }
};

export const deleteWeightEntry = async (entryId) => {
  try {
    const { error } = await supabase
      .from('weight_tracking')
      .delete()
      .eq('id', entryId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting weight entry:', error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// GOALS & HABITS
// =====================================================

export const getUserGoals = async (userEmail) => {
  try {
    const { data, error } = await supabase.rpc('get_user_goals', {
      p_user_email: userEmail
    });

    if (error) throw error;
    return { success: true, data: data || null };
  } catch (error) {
    console.error('Error fetching goals:', error);
    return { success: false, error: error.message, data: null };
  }
};

export const saveUserGoals = async (userEmail, goalData) => {
  try {
    const { error } = await supabase.rpc('save_user_goals', {
      p_user_email: userEmail,
      p_goal_data: goalData
    });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error saving goals:', error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// PROGRAM COMPLETIONS
// =====================================================

export const getProgramCompletions = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('program_completions')
      .select('program_id, completion_data')
      .eq('user_email', userEmail);

    if (error) throw error;
    
    // Convert to object format { programId: [completions] }
    const completions = {};
    data?.forEach(item => {
      completions[item.program_id] = item.completion_data || [];
    });
    
    return { success: true, data: completions };
  } catch (error) {
    console.error('Error fetching program completions:', error);
    return { success: false, error: error.message, data: {} };
  }
};

export const saveProgramCompletion = async (userEmail, programId, completionData) => {
  try {
    const { error } = await supabase
      .from('program_completions')
      .upsert({
        user_email: userEmail,
        program_id: programId,
        completion_data: completionData,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_email,program_id' });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error saving program completion:', error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// CYCLE TRACKING
// =====================================================

export const getCycleData = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('cycle_tracking')
      .select('cycle_data')
      .eq('user_email', userEmail)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return { success: true, data: data?.cycle_data || null };
  } catch (error) {
    console.error('Error fetching cycle data:', error);
    return { success: false, error: error.message, data: null };
  }
};

export const saveCycleData = async (userEmail, cycleData) => {
  try {
    const { error } = await supabase
      .from('cycle_tracking')
      .upsert({
        user_email: userEmail,
        cycle_data: cycleData,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_email' });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error saving cycle data:', error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// WORKOUT HISTORY & EXERCISE PRs
// =====================================================

export const getWorkoutHistory = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('workout_history')
      .select('history_data')
      .eq('user_email', userEmail)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return { success: true, data: data?.history_data || {} };
  } catch (error) {
    console.error('Error fetching workout history:', error);
    return { success: false, error: error.message, data: {} };
  }
};

export const saveWorkoutHistory = async (userEmail, historyData) => {
  try {
    const { error } = await supabase
      .from('workout_history')
      .upsert({
        user_email: userEmail,
        history_data: historyData,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_email' });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error saving workout history:', error);
    return { success: false, error: error.message };
  }
};

export const getExercisePRs = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('exercise_prs')
      .select('pr_data')
      .eq('user_email', userEmail)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return { success: true, data: data?.pr_data || {} };
  } catch (error) {
    console.error('Error fetching exercise PRs:', error);
    return { success: false, error: error.message, data: {} };
  }
};

export const saveExercisePRs = async (userEmail, prData) => {
  try {
    const { error } = await supabase
      .from('exercise_prs')
      .upsert({
        user_email: userEmail,
        pr_data: prData,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_email' });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error saving exercise PRs:', error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// WEIGHT TRACKING SETTINGS
// =====================================================

export const getWeightSettings = async (userEmail) => {
  try {
    const { data, error } = await supabase.rpc('get_weight_settings', {
      p_user_email: userEmail
    });

    if (error) throw error;
    return { 
      success: true, 
      data: data || { goal_type: 'lose', starting_weight: '', target_weight: '' }
    };
  } catch (error) {
    console.error('Error fetching weight settings:', error);
    return { success: false, error: error.message, data: null };
  }
};

export const saveWeightSettings = async (userEmail, settings) => {
  try {
    const { error } = await supabase.rpc('save_weight_settings', {
      p_user_email: userEmail,
      p_goal_type: settings.goalType,
      p_starting_weight: settings.startingWeight,
      p_target_weight: settings.targetWeight
    });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error saving weight settings:', error);
    return { success: false, error: error.message };
  }
};

export const getWeightEntries = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('weight_entries')
      .select('*')
      .eq('user_email', userEmail)
      .order('date', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching weight entries:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const addWeightEntrySync = async (userEmail, entry) => {
  try {
    const { data, error } = await supabase
      .from('weight_entries')
      .insert({
        user_email: userEmail,
        weight: entry.weight,
        date: entry.date,
        notes: entry.notes || ''
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error adding weight entry:', error);
    return { success: false, error: error.message };
  }
};

export const deleteWeightEntrySync = async (entryId) => {
  try {
    const { error } = await supabase
      .from('weight_entries')
      .delete()
      .eq('id', entryId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting weight entry:', error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// REFLECTIONS
// =====================================================

export const getReflections = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('reflections')
      .select('*')
      .eq('user_email', userEmail)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching reflections:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const addReflection = async (userEmail, reflection) => {
  try {
    const { data, error } = await supabase
      .from('reflections')
      .insert({
        user_email: userEmail,
        category: reflection.category,
        prompt: reflection.prompt,
        answer: reflection.answer
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error adding reflection:', error);
    return { success: false, error: error.message };
  }
};

export const deleteReflection = async (reflectionId) => {
  try {
    const { error } = await supabase
      .from('reflections')
      .delete()
      .eq('id', reflectionId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting reflection:', error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// USER PREFERENCES (UI settings, milestones, etc.)
// =====================================================

export const getUserPreferences = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('preferences_data')
      .eq('user_email', userEmail)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return { success: true, data: data?.preferences_data || {} };
  } catch (error) {
    console.error('Error fetching user preferences:', error);
    return { success: false, error: error.message, data: {} };
  }
};

export const saveUserPreferences = async (userEmail, preferences) => {
  try {
    const { error } = await supabase
      .from('user_preferences')
      .upsert({
        user_email: userEmail,
        preferences_data: preferences,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_email' });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error saving user preferences:', error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// WELCOME MODAL STATUS
// =====================================================

export const getWelcomeModalStatus = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('welcome_modal_status')
      .select('has_seen')
      .eq('user_email', userEmail)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return { success: true, hasSeen: data?.has_seen || false };
  } catch (error) {
    console.error('Error fetching welcome modal status:', error);
    return { success: false, error: error.message, hasSeen: false };
  }
};

export const markWelcomeModalAsSeen = async (userEmail) => {
  try {
    const { error } = await supabase
      .from('welcome_modal_status')
      .upsert({
        user_email: userEmail,
        has_seen: true,
        seen_at: new Date().toISOString()
      }, { onConflict: 'user_email' });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error marking welcome modal as seen:', error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// PROGRESS PHOTOS
// =====================================================

export const getProgressEntries = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('progress_entries')
      .select('*')
      .eq('user_email', userEmail)
      .order('date', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching progress entries:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const addProgressEntry = async (userEmail, entry) => {
  try {
    const { data, error } = await supabase
      .from('progress_entries')
      .insert({
        user_email: userEmail,
        date: entry.date,
        weight: entry.weight || null,
        photo_url: entry.photoUrl || null,
        notes: entry.notes || ''
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error adding progress entry:', error);
    return { success: false, error: error.message };
  }
};

export const updateProgressEntry = async (entryId, updates) => {
  try {
    const { data, error } = await supabase
      .from('progress_entries')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', entryId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error updating progress entry:', error);
    return { success: false, error: error.message };
  }
};

export const deleteProgressEntry = async (entryId) => {
  try {
    const { error } = await supabase
      .from('progress_entries')
      .delete()
      .eq('id', entryId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting progress entry:', error);
    return { success: false, error: error.message };
  }
};

export const uploadProgressPhoto = async (userEmail, file) => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userEmail}/${Date.now()}.${fileExt}`;
    
    const { data, error } = await supabase.storage
      .from('progress-photos')
      .upload(fileName, file);

    if (error) throw error;
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from('progress-photos')
      .getPublicUrl(fileName);
    
    return { success: true, url: urlData.publicUrl };
  } catch (error) {
    console.error('Error uploading progress photo:', error);
    return { success: false, error: error.message };
  }
};

export const deleteProgressPhoto = async (photoUrl) => {
  try {
    // Extract path from URL
    const path = photoUrl.split('/progress-photos/')[1];
    if (!path) return { success: true };
    
    const { error } = await supabase.storage
      .from('progress-photos')
      .remove([path]);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting progress photo:', error);
    return { success: false, error: error.message };
  }
};
